/**
 * ContactDrawer — right-side add/edit form for a directory contact
 * (ADR-141 §4).
 *
 * Local state (not react-hook-form): the destinations editor is a dynamic
 * row list with positional priority, which maps more naturally onto plain
 * arrays than a RHF field array, and the save path is an orchestrated
 * multi-endpoint sequence (`useSaveContact`) rather than a single POST.
 *
 * Validation is client-side *hints* only (per address kind — E.164 /
 * email / HTTPS webhook); the server's `ContactDestination.clean()`
 * remains the authority and its errors surface via the failure toast.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Box,
  Field,
  Flex,
  IconButton,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  HiChevronDown,
  HiChevronUp,
  HiTrash,
  HiXMark,
} from 'react-icons/hi2';
import {
  Button as ButtonComponent,
  SelectInput,
  ToggleButton,
} from '@themes/aegix';
import { DrawerComponent } from '../../ui/drawerComponent';
import { toaster } from '../../ui/toaster';
import {
  ADMIN_SELECTABLE_CHANNEL_TYPES,
  addressPlaceholder,
  channelLabel,
  channelOption,
  validateDestinationAddress,
  type Contact,
  type ContactTag,
} from '../../../services/contacts.service';
import { InputField } from '@themes/aegix';
import {
  useSaveContact,
  type DestinationDraft,
} from '../../../hooks/queries/useContacts';

interface DestinationRow extends DestinationDraft {
  /** Stable local key for React list identity (new rows have no id). */
  key: string;
}

interface ContactDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Persisted contact when editing; null when adding. */
  contact: Contact | null;
  /** Org tag catalog for the multi-select. */
  tags: ContactTag[];
}

let localKeyCounter = 0;
function nextKey(): string {
  localKeyCounter += 1;
  return `row-${localKeyCounter}`;
}

function rowsFromContact(contact: Contact | null): DestinationRow[] {
  if (!contact) {
    return [];
  }
  return [...contact.destinations]
    .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
    .map((destination) => ({
      key: nextKey(),
      id: destination.id,
      channel_code: destination.channel_code,
      address: destination.address ?? '',
      enabled: destination.enabled ?? true,
    }));
}

export function ContactDrawer({
  isOpen,
  onClose,
  contact,
  tags,
}: ContactDrawerProps) {
  const { t } = useTranslation();
  const saveContact = useSaveContact();

  const [displayName, setDisplayName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [rows, setRows] = useState<DestinationRow[]>([]);
  const [tagIds, setTagIds] = useState<number[]>([]);
  const [showErrors, setShowErrors] = useState(false);

  // (Re)initialize whenever the drawer opens for a different contact.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setDisplayName(contact?.display_name ?? '');
    setIsActive(contact?.is_active !== false);
    setRows(rowsFromContact(contact));
    setTagIds(contact?.tags.map((assignment) => assignment.tag) ?? []);
    setShowErrors(false);
  }, [isOpen, contact]);

  const nameError = displayName.trim() ? null : t('contacts.nameRequired', 'Name is required');
  const rowErrors = useMemo(
    () =>
      rows.map((row) =>
        validateDestinationAddress(row.channel_code, row.address),
      ),
    [rows],
  );
  const hasErrors = nameError !== null || rowErrors.some((e) => e !== null);

  const updateRow = (key: string, patch: Partial<DestinationRow>) => {
    setRows((prev) =>
      prev.map((row) => (row.key === key ? { ...row, ...patch } : row)),
    );
  };

  const moveRow = (index: number, delta: -1 | 1) => {
    setRows((prev) => {
      const target = index + delta;
      if (target < 0 || target >= prev.length) {
        return prev;
      }
      const next = [...prev];
      const [moved] = next.splice(index, 1);
      next.splice(target, 0, moved);
      return next;
    });
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        key: nextKey(),
        channel_code: ADMIN_SELECTABLE_CHANNEL_TYPES[0].code,
        address: '',
        enabled: true,
      },
    ]);
  };

  const removeRow = (key: string) => {
    setRows((prev) => prev.filter((row) => row.key !== key));
  };

  const selectedTags = useMemo(
    () => tags.filter((tag) => tagIds.includes(tag.id)),
    [tags, tagIds],
  );
  const availableTagOptions = useMemo(
    () =>
      tags
        .filter((tag) => !tagIds.includes(tag.id))
        .map((tag) => ({ label: tag.name, value: String(tag.id) })),
    [tags, tagIds],
  );

  const handleSave = async () => {
    setShowErrors(true);
    if (hasErrors || saveContact.isPending) {
      return;
    }
    try {
      await saveContact.mutateAsync({
        contact: { display_name: displayName.trim(), is_active: isActive },
        destinations: rows.map((row) => ({
          id: row.id,
          channel_code: row.channel_code,
          address: row.address,
          enabled: row.enabled,
        })),
        tagIds,
        original: contact,
      });
      toaster.create({
        title: contact ? t('contacts.contactUpdated', 'Contact has been updated') : t('contacts.contactCreated', 'Contact has been created'),
        type: 'success',
      });
      onClose();
    } catch {
      toaster.create({
        title: contact ? t('contacts.failedUpdateContact', 'Failed to update contact') : t('contacts.failedCreateContact', 'Failed to create contact'),
        type: 'error',
      });
    }
  };

  return (
    <DrawerComponent
      isOpen={isOpen}
      onClose={onClose}
      title={contact ? t('contacts.editContact', 'Edit Contact') : t('contacts.addContact', 'Add Contact')}
      size="lg"
      closeOnOutside={false}
      closeOnEsc={false}
      e2eTitle="contactDrawer"
      footer={
        <SimpleGrid columns={2} gap={4} width="100%">
          <ButtonComponent
            visual="secondary"
            type="button"
            onClick={onClose}
            data-e2e="cancel-button"
          >
            {t('common.cancel', 'Cancel')}
          </ButtonComponent>
          <ButtonComponent
            visual="primary"
            type="button"
            onClick={handleSave}
            disabled={saveContact.isPending}
            data-e2e="saveContact"
          >
            {t('common.save', 'Save')}
          </ButtonComponent>
        </SimpleGrid>
      }
    >
      <VStack gap={4} align="stretch">
        <Field.Root invalid={showErrors && !!nameError}>
          <Field.Label htmlFor="contact-display-name">{t('common.name', 'Name')}</Field.Label>
          <InputField
            id="contact-display-name"
            name="display_name"
            placeholder={t('contacts.fullNamePlaceholder', 'Full name')}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            data-e2e="contactNameInput"
          />
          {showErrors && nameError && (
            <Field.ErrorText data-e2e="contactNameError">
              {nameError}
            </Field.ErrorText>
          )}
        </Field.Root>

        <ToggleButton
          title={t('common.active', 'Active')}
          e2eTitle="contactActiveToggle"
          checked={isActive}
          onCheckedChange={() => setIsActive((prev) => !prev)}
        />

        {/* ------------------------------------------------------------- */}
        {/* Destinations                                                   */}
        {/* ------------------------------------------------------------- */}
        <Box>
          <Text fontSize="md" fontWeight={600} color="text.primary" mb={1}>
            {t('contacts.destinations', 'Destinations')}
          </Text>
          <Text fontSize="sm" color="text.secondary" mb={2}>
            {t('contacts.destinationsSubtitle', 'Order sets priority (first = highest). Alerts go to every enabled destination.')}
          </Text>
          <VStack gap={3} align="stretch" data-e2e="destinationsEditor">
            {rows.map((row, index) => {
              const isInternal =
                channelOption(row.channel_code)?.addressKind === 'internal';
              const error = rowErrors[index];
              return (
                <Flex
                  key={row.key}
                  gap={2}
                  align="flex-start"
                  borderWidth="1px"
                  borderColor="border.tertiary"
                  borderRadius="md"
                  p={2}
                  data-e2e="destinationRow"
                >
                  <VStack gap={0}>
                    <IconButton
                      aria-label={t('contacts.moveDestinationUp', 'Move destination up')}
                      size="2xs"
                      variant="ghost"
                      disabled={index === 0}
                      onClick={() => moveRow(index, -1)}
                      data-e2e="destinationMoveUp"
                    >
                      <HiChevronUp />
                    </IconButton>
                    <IconButton
                      aria-label={t('contacts.moveDestinationDown', 'Move destination down')}
                      size="2xs"
                      variant="ghost"
                      disabled={index === rows.length - 1}
                      onClick={() => moveRow(index, 1)}
                      data-e2e="destinationMoveDown"
                    >
                      <HiChevronDown />
                    </IconButton>
                  </VStack>

                  {isInternal ? (
                    <Box flex={1} pt={1}>
                      <Text fontSize="sm" color="text.primary">
                        {channelLabel(row.channel_code)}
                      </Text>
                      <Text fontSize="sm" color="text.secondary">
                        {t('contacts.deliveredViaApp', "Delivered via the linked user's app")}
                      </Text>
                    </Box>
                  ) : (
                    <>
                      <Box w="40%">
                        <SelectInput
                          label={t('contacts.channel', 'Channel')}
                          id={`destination-channel-${row.key}`}
                          e2eTitle="destinationChannelSelect"
                          searchable={false}
                          option={ADMIN_SELECTABLE_CHANNEL_TYPES.map((c) => ({
                            label: c.label,
                            value: c.code,
                          }))}
                          value={row.channel_code}
                          onChange={(value: string) =>
                            updateRow(row.key, { channel_code: value })
                          }
                        />
                      </Box>
                      <Field.Root flex={1} invalid={showErrors && !!error}>
                        <Field.Label
                          htmlFor={`destination-address-${row.key}`}
                        >
                          {t('contacts.address', 'Address')}
                        </Field.Label>
                        <InputField
                          id={`destination-address-${row.key}`}
                          placeholder={addressPlaceholder(row.channel_code)}
                          value={row.address}
                          onChange={(e) =>
                            updateRow(row.key, { address: e.target.value })
                          }
                          data-e2e="destinationAddressInput"
                        />
                        {showErrors && error && (
                          <Field.ErrorText data-e2e="destinationAddressError">
                            {error}
                          </Field.ErrorText>
                        )}
                      </Field.Root>
                    </>
                  )}

                  <Box w={16} pt={1}>
                    <ToggleButton
                      e2eTitle="destinationEnabledToggle"
                      checked={row.enabled}
                      onCheckedChange={() =>
                        updateRow(row.key, { enabled: !row.enabled })
                      }
                    />
                  </Box>
                  <IconButton
                    aria-label={t('contacts.removeDestination', 'Remove destination')}
                    size="xs"
                    variant="ghost"
                    colorPalette="red"
                    onClick={() => removeRow(row.key)}
                    data-e2e="removeDestination"
                  >
                    <HiTrash />
                  </IconButton>
                </Flex>
              );
            })}
          </VStack>
          <Box mt={2}>
            <ButtonComponent
              visual="secondary"
              type="button"
              onClick={addRow}
              data-e2e="addDestination"
            >
              {t('contacts.addDestination', 'Add destination')}
            </ButtonComponent>
          </Box>
        </Box>

        {/* ------------------------------------------------------------- */}
        {/* Tags                                                           */}
        {/* ------------------------------------------------------------- */}
        <Box>
          <Text fontSize="md" fontWeight={600} color="text.primary" mb={2}>
            {t('contacts.tags', 'Tags')}
          </Text>
          {selectedTags.length > 0 && (
            <Flex wrap="wrap" gap={2} mb={2} data-e2e="contactDrawerTags">
              {selectedTags.map((tag) => (
                // eslint-disable-next-line aegix/no-inline-status-pill -- tag chip (removable label), not a status pill; StatusBadge's status color map does not apply
                <Badge
                  key={tag.id}
                  variant="subtle"
                  colorPalette="blue"
                  display="inline-flex"
                  alignItems="center"
                  gap={1}
                  data-e2e="contactDrawerTagChip"
                >
                  {tag.name}
                  <IconButton
                    aria-label={`Remove tag ${tag.name}`}
                    size="2xs"
                    variant="plain"
                    minW="auto"
                    h="auto"
                    onClick={() =>
                      setTagIds((prev) => prev.filter((id) => id !== tag.id))
                    }
                    data-e2e="removeContactTag"
                  >
                    <HiXMark />
                  </IconButton>
                </Badge>
              ))}
            </Flex>
          )}
          <SelectInput
            label={t('contacts.addTag', 'Add tag')}
            id="contact-tag-select"
            e2eTitle="contactTagSelect"
            option={availableTagOptions}
            value=""
            placeholder={t('contacts.selectTagPlaceholder', 'Select a tag')}
            onChange={(value: string) => {
              const tagId = Number(value);
              if (Number.isFinite(tagId) && tagId > 0) {
                setTagIds((prev) =>
                  prev.includes(tagId) ? prev : [...prev, tagId],
                );
              }
            }}
          />
        </Box>
      </VStack>
    </DrawerComponent>
  );
}
